import {inject, Injectable} from "@angular/core";
import {AggregatedDeviceState} from "src/app/domain/devices/aggregated-state/aggregated-device-state";
import {DataStorageService} from "src/app/infrastructure/data-storage.service";
import {AggregatedDeviceStateStore} from "src/app/domain/devices/aggregated-state/aggregated-device-state.store";
import {map, Observable, tap} from "rxjs";
import {FarmStore} from "src/app/domain/assets/farm/farm.store";
import {DeviceStore} from "src/app/domain/devices/device.store";
import {
  AggregatedDeviceStateApiClient,
  RawAggregatedDeviceState
} from "src/app/infrastructure/moovement-api/aggregated-device-state.api-client";

@Injectable({
  providedIn: 'root'
})
export class AggregatedDeviceStateService {

  private aggregatedDeviceStateStore = inject(AggregatedDeviceStateStore);
  private deviceStore = inject(DeviceStore);
  private farmStore = inject(FarmStore);

  constructor(
    private aggregatedDeviceStateApiClient: AggregatedDeviceStateApiClient,
    private dataStorageService: DataStorageService
  ) {}

  reloadAggregatedDeviceStates(deviceIds: string[]): Observable<void> {
    return this.aggregatedDeviceStateApiClient.getAggregatedDeviceStates(deviceIds).pipe(
      tap(states => {
          if (!states || states.length === 0) return;
          states.forEach(state => this.aggregatedDeviceStateStore.setEntity(this.toState(state)));
      }),
      map(_ => undefined)
    );
  }

  reloadAggregatedDeviceState(deviceId: string): Observable<void> {
    return this.reloadAggregatedDeviceStates([deviceId]);
  }

  getAggregatedDeviceStates(farmId: string, deviceIds: string[]) {
    this.aggregatedDeviceStateApiClient.getAggregatedDeviceStates(deviceIds).subscribe({
      next: async aggregatedDeviceStates => {
        this.aggregatedDeviceStateStore.setAllEntities(this.toStates(aggregatedDeviceStates));
        await this.dataStorageService.addKeyValuePair(`aggregatedDeviceStates-${farmId}`, aggregatedDeviceStates);
      },
      error: async () => {
        const cachedStates = await this.dataStorageService.getValueByKey<RawAggregatedDeviceState[]>(`aggregatedDeviceStates-${farmId}`);
        if (!cachedStates) return;
        this.aggregatedDeviceStateStore.setAllEntities(this.toStates(cachedStates));
      }
    });
  }

  private toStates(rawStates: RawAggregatedDeviceState[]): AggregatedDeviceState[] {
    return rawStates.map(rawState => this.toState(rawState));
  }

  private toState(rawState: RawAggregatedDeviceState): AggregatedDeviceState {
    return {
        id: rawState.id,
        lastSeenByGatewayAt: this.toDate(rawState.lastSeenByGatewayAt),
        lastSeenByBLEScannerAt: this.toDate(rawState.lastSeenByBLEScannerAt),
        lastGPSFixAt: this.toDate(rawState.lastGPSFixAt),
        lastObservationPerDevice: this.toLastObservationPerDevice(rawState.lastObservationPerDevice),
        lastRssiPerDevice: this.toLastRssiPerDevice(rawState.lastRssiPerDevice),
        associatedDevices: this.toAssociatedDevices(rawState.associatedDevices),
        lastBatteryVoltageAt: this.toDate(rawState.lastBatteryVoltageAt),
        batteryVoltage: rawState.batteryVoltage
      };
  }

  private toLastObservationPerDevice(rawLastObservationPerDevice: {[key: string]: string}) {
    const result = new Map();
    Object.keys(rawLastObservationPerDevice).forEach(key => {
      result.set(key, this.toDate(rawLastObservationPerDevice[key]));
    });

    return result;
  }

  private toLastRssiPerDevice(rawLastRssiPerDevice: {[key: string]: number}) {
    const result = new Map();
    Object.keys(rawLastRssiPerDevice).forEach(key => {
      result.set(key, rawLastRssiPerDevice[key]);
    });

    return result;
  }

  private toAssociatedDevices(rawAssociatedDevices: {[key: string]: string}) {
    const result = new Map();
    Object.keys(rawAssociatedDevices).forEach(key => {
      result.set(key, rawAssociatedDevices[key]);
    });

    return result;
  }

  private toDate(value?: string) {
    if (!value) return undefined;

    return new Date(value);
  }
}
